import {
    ExternalArticle,
    ExternalArticleMetadata,
    ExternalArticleType,
    Languages,
    LibrarySubject,
    Keyword,
} from '@livv/models';
import { stripHtmlTags } from './text';

/** Classification follows the original article language, independently of the UI/summary. */
export const getExternalArticleKeyword = (
    article: Pick<ExternalArticle, 'language' | 'keyword' | 'keywordEn'>,
): Keyword =>
    article.language === Languages.en && article.keywordEn?.treeId
        ? article.keywordEn
        : article.keyword;

export const getExternalPublicationId = ({
    type,
    publication,
}: Pick<ExternalArticle, 'type' | 'publication'>): string => {
    const title = publication.title
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

    return `${type}-${title}${type === ExternalArticleType.MELANGES ? `-${publication.year}` : ''}`;
};

/** Empty rich-text editor paragraphs and nonbreaking spaces are not summary content. */
export const hasExternalArticleSummaryText = (summary?: string | null): boolean =>
    !!summary &&
    !!stripHtmlTags(summary)
        .replace(/&nbsp;|&#0*160;|&#x0*a0;/gi, ' ')
        .trim();

/** Prefer the app language, then the available short summary; never expose detailed text. */
export const getExternalArticleShortSummary = (
    article: Pick<ExternalArticle, 'language' | 'summaryShortFr' | 'summaryShortEn'>,
    language: string = article.language,
): { summaryShort: string; summaryLanguage: Languages } => {
    const summaries = [
        { summaryShort: article.summaryShortFr || '', summaryLanguage: Languages.fr },
        { summaryShort: article.summaryShortEn || '', summaryLanguage: Languages.en },
    ].filter(({ summaryShort }) => hasExternalArticleSummaryText(summaryShort));

    return (
        summaries.find((summary) => summary.summaryLanguage === language) ||
        summaries[0] || {
            summaryShort: '',
            summaryLanguage: article.language,
        }
    );
};

export const groupExternalArticlesBySubject = (
    articles: ExternalArticleMetadata[],
    treeTitles: Record<string, string>,
): LibrarySubject[] => {
    const subjects = new Map<string, LibrarySubject>();
    for (const article of articles) {
        if (article.isPublished !== true) continue;
        const { treeId } = getExternalArticleKeyword(article);
        const title = treeTitles[treeId];
        if (!title) throw new Error(`Missing keyword book ${treeId} for article ${article.id}`);
        let subject = subjects.get(treeId);
        if (!subject) {
            subject = { id: treeId, title, years: [], articleCount: 0, facets: [] };
            subjects.set(subject.id, subject);
        }
        subject.articleCount++;
        subject.years = [...new Set([...subject.years, article.publication.year])].sort(
            (a, b) => a - b,
        );
        const facet = subject.facets.find(
            (item) =>
                item.year === article.publication.year &&
                item.language === article.language &&
                item.type === article.type,
        );
        if (facet) facet.count++;
        else
            subject.facets.push({
                year: article.publication.year,
                language: article.language,
                type: article.type,
                count: 1,
            });
    }

    return [...subjects.values()];
};
